import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import signalCampaignService, {
  SignalCampaign,
  SignalCampaignContact,
  AbResults,
  LemlistCampaignOption,
  AddSignalContactInput,
} from '../services/signalCampaignService';
import { PageHeader, useConfirm, RelatedLinks, type RelatedLinkItem } from '../components/ui';
import { gtmPfad } from '../vertrieb/gtmLinks';
import { useKampagnenZuordnung, kampagnenLinks } from '../vertrieb/kampagnen';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { RechteHinweis } from '../vertrieb/RechteHinweis';

const SIGNAL_BADGES: Record<string, { label: string; className: string }> = {
  pending: { label: 'ausstehend', className: 'badge-secondary' },
  fetched: { label: 'erfasst', className: 'badge-success' },
  no_post: { label: 'kein Post', className: 'badge-warning' },
  error: { label: 'Fehler', className: 'badge-error' },
};

const STATUS_BADGES: Record<string, { label: string; className: string }> = {
  new: { label: 'neu', className: 'badge-secondary' },
  signal_ready: { label: 'Signal bereit', className: 'badge-info' },
  message_ready: { label: 'Nachricht bereit', className: 'badge-info' },
  sent: { label: 'gesendet', className: 'badge-success' },
  opened: { label: 'geöffnet', className: 'badge-success' },
  replied: { label: 'geantwortet', className: 'badge-success' },
  bounced: { label: 'Bounce', className: 'badge-error' },
};

const EMPTY_CONTACT: AddSignalContactInput = {
  firstName: '',
  lastName: '',
  email: '',
  company: '',
  jobTitle: '',
  linkedInUrl: '',
  linkedInUrn: '',
  signalText: '',
};

const pct = (n: number) => `${(n * 100).toFixed(1)}%`;

const SignalCampaignDetailPage: React.FC = () => {
  const { confirm } = useConfirm();
  // Welle 2: Pipeline-Schritte, Zuordnung, Prospects und Bewertung sind
  // Schreibaufrufe (vertrieb.bearbeiten), "An Lemlist senden" verlangt
  // versand.ausfuehren; GET /lemlist/campaigns nur mit versand.lesen.
  const { darf } = useBerechtigung();
  const darfBearbeiten = darf('vertrieb.bearbeiten');
  const darfSenden = darf('versand.ausfuehren');
  const siehtLemlist = darf('versand.lesen');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState<SignalCampaign | null>(null);
  const [abResults, setAbResults] = useState<AbResults | null>(null);
  const [lemlistCampaigns, setLemlistCampaigns] = useState<LemlistCampaignOption[]>([]);
  const zuordnung = useKampagnenZuordnung();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newContact, setNewContact] = useState<AddSignalContactInput>(EMPTY_CONTACT);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [c, ab] = await Promise.all([
        signalCampaignService.getCampaign(id),
        signalCampaignService.getAbResults(id),
      ]);
      setCampaign(c);
      setAbResults(ab);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kampagne konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
    if (siehtLemlist) signalCampaignService.getLemlistCampaigns().then(setLemlistCampaigns).catch(() => undefined);
  }, [load, siehtLemlist]);

  const runStep = async (step: string, fn: () => Promise<string>) => {
    setBusy(step);
    setError('');
    setMessage('');
    try {
      const summary = await fn();
      setMessage(summary);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || `Schritt fehlgeschlagen: ${step}`);
    } finally {
      setBusy(null);
    }
  };

  const handleFetchSignals = () =>
    runStep('signals', async () => {
      const r = await signalCampaignService.fetchSignals(id!);
      return `Signale: ${r.fetched} erfasst, ${r.noPost} ohne Post, ${r.errors} Fehler.`;
    });

  const handleGenerate = () =>
    runStep('generate', async () => {
      const r = await signalCampaignService.generateMessages(id!);
      return `Nachrichten: ${r.generated} generiert, ${r.failed} fehlgeschlagen.`;
    });

  const handleSync = () =>
    runStep('sync', async () => {
      const r = await signalCampaignService.syncToLemlist(id!);
      return `Lemlist: ${r.synced} gesendet, ${r.failed} fehlgeschlagen.`;
    });

  const handleTrack = () =>
    runStep('track', async () => {
      const r = await signalCampaignService.trackOutcomes(id!);
      return `Tracking: ${r.updated} Kontakte aktualisiert.`;
    });

  const handleAssignLemlist = async (lemlistCampaignId: string) => {
    if (!id) return;
    try {
      await signalCampaignService.updateCampaign(id, { lemlistCampaignId });
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Zuordnung fehlgeschlagen');
    }
  };

  const handleAddContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setError('');
    try {
      const cleaned: AddSignalContactInput = Object.fromEntries(
        Object.entries(newContact).filter(([, v]) => v !== '')
      );
      await signalCampaignService.addContacts(id, [cleaned]);
      setNewContact(EMPTY_CONTACT);
      setShowAddForm(false);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kontakt konnte nicht hinzugefügt werden');
    }
  };

  const handleOutcome = async (contactId: string, outcome: string) => {
    try {
      await signalCampaignService.updateContact(contactId, { outcome });
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Bewertung fehlgeschlagen');
    }
  };

  const handleDeleteContact = async (contact: SignalCampaignContact) => {
    const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.email || 'Kontakt';
    const ok = await confirm({
      title: `„${name}“ aus der Kampagne löschen?`,
      message: 'Der Kontakt wird mit seinem erfassten Signal und seiner Nachricht aus der Kampagne entfernt. Rückgängig nur durch erneutes Hinzufügen.',
      confirmLabel: 'Löschen',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await signalCampaignService.deleteContact(contact.id);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Löschen fehlgeschlagen');
    }
  };

  const variantLabel = (variantId?: string | null) =>
    campaign?.variants?.find((v) => v.id === variantId)?.label || '–';

  const badge = (map: Record<string, { label: string; className: string }>, key: string) => {
    const info = map[key] || { label: key, className: 'badge-secondary' };
    return <span className={`badge ${info.className}`}>{info.label}</span>;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <div className="loading-text">Lade Kampagne...</div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="page-container">
        <div className="alert alert-error">{error || 'Kampagne nicht gefunden'}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/signal-campaigns')}>
          Zurück
        </button>
      </div>
    );
  }

  const contacts: SignalCampaignContact[] = campaign.contacts || [];

  return (
    <div className="page-container">
      <PageHeader
        eyebrow={(
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/signal-campaigns')}>
            ← Zurück
          </button>
        )}
        title={campaign.name}
        subtitle={campaign.description || undefined}
      />
      {campaign.goal && (
        <p className="text-sm" style={{ marginTop: '0.25rem' }}>
          <strong>Ziel:</strong> {campaign.goal}
        </p>
      )}

      <RechteHinweis was="Signale abrufen, Nachrichten erzeugen und Prospects pflegen" />

      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      {/* Lemlist assignment */}
      <div className="card" style={{ marginTop: '1rem' }}>
        {siehtLemlist ? (
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Lemlist-Kampagne (für Versand)</label>
            <select
              className="form-input"
              value={campaign.lemlistCampaignId || ''}
              disabled={!darfBearbeiten}
              onChange={(e) => handleAssignLemlist(e.target.value)}
            >
              <option value="">– nicht zugeordnet –</option>
              {lemlistCampaigns.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <p className="text-sm" style={{ margin: 0 }}>
            Lemlist-Kampagne (für Versand): {campaign.lemlistCampaignId ? 'zugeordnet' : 'nicht zugeordnet'}
          </p>
        )}
        {(() => {
          // Grundlagen aus dem GTM-Setup und alles an derselben Lemlist-Kampagne.
          const items: RelatedLinkItem[] = [
            campaign.icpId && { key: 'icp', label: 'Zielkundenprofil', icon: 'target' as const, to: gtmPfad('icp', campaign.icpId) },
            campaign.personaId && { key: 'persona', label: 'Persona', icon: 'user' as const, to: gtmPfad('persona', campaign.personaId) },
            campaign.offerId && { key: 'offer', label: 'Angebot', icon: 'briefcase' as const, to: gtmPfad('offer', campaign.offerId) },
            ...(campaign.lemlistCampaignId
              ? kampagnenLinks(campaign.lemlistCampaignId, zuordnung, { leads: true }).filter((l) => l.key !== `signal-${campaign.id}`)
              : []),
          ].filter(Boolean) as RelatedLinkItem[];
          return items.length > 0 ? <RelatedLinks className="mt-3" items={items} label="Verknüpft mit dieser Kampagne" /> : null;
        })()}
      </div>

      {/* Pipeline -- jeder Schritt schreibt, ohne Schreibrecht entfaellt die Karte. */}
      {darfBearbeiten && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <h3 className="card-title">Pipeline</h3>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button className="btn btn-info" disabled={!!busy} onClick={handleFetchSignals}>
              {busy === 'signals' ? 'Lädt…' : '1. Signale abrufen'}
            </button>
            <button className="btn btn-info" disabled={!!busy} onClick={handleGenerate}>
              {busy === 'generate' ? 'Generiert…' : '2. Nachrichten generieren (Claude)'}
            </button>
            {darfSenden && (
              <button
                className="btn btn-primary"
                disabled={!!busy || !campaign.lemlistCampaignId}
                onClick={handleSync}
                title={!campaign.lemlistCampaignId ? 'Zuerst Lemlist-Kampagne zuordnen' : ''}
              >
                {busy === 'sync' ? 'Sendet…' : '3. An Lemlist senden'}
              </button>
            )}
            <button
              className="btn btn-secondary"
              disabled={!!busy || !campaign.lemlistCampaignId}
              onClick={handleTrack}
            >
              {busy === 'track' ? 'Aktualisiert…' : '4. Ergebnisse aktualisieren'}
            </button>
          </div>
          <p className="text-xs text-muted" style={{ marginTop: '0.75rem' }}>
            Signale stammen aus der eigenen LinkedIn-Verbindung (letzter Post je Prospect).
            Ohne LinkedIn-URN kann der Post-Text alternativ manuell eingetragen werden.
          </p>
          <RechteHinweis recht="versand.ausfuehren" was="An Lemlist senden" className="text-xs text-muted" />
        </div>
      )}

      {/* A/B results */}
      {abResults && abResults.variants.length > 0 && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <h3 className="card-title">A/B-Auswertung</h3>
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Variante</th>
                  <th>Zugewiesen</th>
                  <th>Gesendet</th>
                  <th>Öffnungsrate</th>
                  <th>Antwortrate</th>
                  <th>Positiv</th>
                </tr>
              </thead>
              <tbody>
                {abResults.variants.map((v) => (
                  <tr key={v.id}>
                    <td>
                      <strong>{v.label}</strong>{' '}
                      {abResults.winnerId === v.id && (
                        <span className="badge badge-success">Gewinner</span>
                      )}
                    </td>
                    <td>{v.assignedCount}</td>
                    <td>{v.sentCount}</td>
                    <td>{pct(v.openRate)}</td>
                    <td>{pct(v.replyRate)}</td>
                    <td>{pct(v.positiveRate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted" style={{ marginTop: '0.5rem' }}>
            {abResults.conclusive
              ? `Aktueller Gewinner: Variante ${abResults.winnerLabel} (höchste Antwortrate).`
              : 'Noch keine belastbare Aussage – mehr Versand/Reaktionen nötig (min. 10 pro Variante).'}
          </p>
        </div>
      )}

      {/* Contacts */}
      <div className="card" style={{ marginTop: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="card-title" style={{ marginBottom: 0 }}>
            Prospects ({contacts.length})
          </h3>
          {darfBearbeiten && (
            <button className="btn btn-success btn-sm" onClick={() => setShowAddForm(!showAddForm)}>
              {showAddForm ? 'Abbrechen' : 'Prospect hinzufügen'}
            </button>
          )}
        </div>

        {showAddForm && darfBearbeiten && (
          <form onSubmit={handleAddContact} style={{ marginTop: '1rem' }}>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ flex: 1, minWidth: '140px' }}>
                <label>Vorname</label>
                <input className="form-input" value={newContact.firstName}
                  onChange={(e) => setNewContact({ ...newContact, firstName: e.target.value })} />
              </div>
              <div className="form-group" style={{ flex: 1, minWidth: '140px' }}>
                <label>Nachname</label>
                <input className="form-input" value={newContact.lastName}
                  onChange={(e) => setNewContact({ ...newContact, lastName: e.target.value })} />
              </div>
              <div className="form-group" style={{ flex: 1, minWidth: '180px' }}>
                <label>E-Mail (für Lemlist)</label>
                <input className="form-input" type="email" value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ flex: 1, minWidth: '140px' }}>
                <label>Unternehmen</label>
                <input className="form-input" value={newContact.company}
                  onChange={(e) => setNewContact({ ...newContact, company: e.target.value })} />
              </div>
              <div className="form-group" style={{ flex: 1, minWidth: '140px' }}>
                <label>Position</label>
                <input className="form-input" value={newContact.jobTitle}
                  onChange={(e) => setNewContact({ ...newContact, jobTitle: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ flex: 1, minWidth: '200px' }}>
                <label>LinkedIn-URL</label>
                <input className="form-input" value={newContact.linkedInUrl}
                  onChange={(e) => setNewContact({ ...newContact, linkedInUrl: e.target.value })}
                  placeholder="https://www.linkedin.com/in/..." />
              </div>
              <div className="form-group" style={{ flex: 1, minWidth: '200px' }}>
                <label>LinkedIn-Personen-URN (optional)</label>
                <input className="form-input" value={newContact.linkedInUrn}
                  onChange={(e) => setNewContact({ ...newContact, linkedInUrn: e.target.value })}
                  placeholder="urn:li:person:..." />
              </div>
            </div>
            <div className="form-group">
              <label>Letzter LinkedIn-Post (manuell, optional)</label>
              <textarea className="form-textarea" rows={3} value={newContact.signalText}
                onChange={(e) => setNewContact({ ...newContact, signalText: e.target.value })}
                placeholder="Falls die API den Post nicht abrufen kann, hier einfügen." />
            </div>
            <div className="card-footer">
              <button type="submit" className="btn btn-primary btn-sm">Hinzufügen</button>
            </div>
          </form>
        )}

        {contacts.length === 0 ? (
          <p className="text-sm text-muted" style={{ marginTop: '1rem' }}>
            Noch keine Prospects.
          </p>
        ) : (
          <div style={{ overflowX: 'auto', marginTop: '1rem' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Prospect</th>
                  <th>Signal</th>
                  <th>Variante</th>
                  <th>Nachricht</th>
                  <th>Status</th>
                  <th>Bewertung</th>
                  {darfBearbeiten && <th></th>}
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div>{[c.firstName, c.lastName].filter(Boolean).join(' ') || '–'}</div>
                      <div className="text-xs text-muted">
                        {c.company ? <Link to={`/companies?suche=${encodeURIComponent(c.company)}`} title="Im CRM suchen">{c.company}</Link> : null}
                        {c.jobTitle ? ` · ${c.jobTitle}` : ''}
                      </div>
                    </td>
                    <td>
                      {badge(SIGNAL_BADGES, c.signalStatus)}
                      {c.signalText && (
                        <div className="text-xs text-muted" title={c.signalText}
                          style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.signalText}
                        </div>
                      )}
                    </td>
                    <td>{variantLabel(c.variantId)}</td>
                    <td>
                      {c.message ? (
                        <span title={c.message}
                          style={{ display: 'inline-block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.message}
                        </span>
                      ) : (
                        <span className="text-muted">–</span>
                      )}
                    </td>
                    <td>{badge(STATUS_BADGES, c.status)}</td>
                    <td>
                      <select
                        className="form-input"
                        style={{ minWidth: 110, padding: '0.25rem' }}
                        value={c.outcome || ''}
                        disabled={!darfBearbeiten}
                        onChange={(e) => handleOutcome(c.id, e.target.value)}
                      >
                        <option value="">–</option>
                        <option value="positive">positiv</option>
                        <option value="neutral">neutral</option>
                        <option value="negative">negativ</option>
                      </select>
                    </td>
                    {darfBearbeiten && (
                      <td>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteContact(c)}>
                          ✕
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignalCampaignDetailPage;
